import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import type { ReactNode } from "react";
import type {
  CustomerAddress,
  CustomerOrder,
  CustomerWithDetails,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { PhoneNumber } from "@/components/phone/PhoneNumber";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { CommentThread } from "@/modules/comments/CommentThread";
import {
  addressLines,
  dateOnly,
  money,
  ordersLabel,
} from "@/modules/contacts/customers/format";
import {
  CustomerNoteEditor,
  CustomerTagsEditor,
} from "@/modules/contacts/customers/screens/CustomerAnnotations";
import { CUSTOMER_KEYS } from "@/modules/contacts/customers/useCustomerAnnotations";

const FINANCIAL_LABEL: Record<string, string> = {
  pending: "Payment pending",
  authorized: "Authorized",
  partially_paid: "Partially paid",
  paid: "Paid",
  partially_refunded: "Partially refunded",
  refunded: "Refunded",
  voided: "Voided",
};

const FULFILMENT_LABEL: Record<string, string> = {
  partial: "Partially fulfilled",
  fulfilled: "Fulfilled",
  restocked: "Restocked",
};

function SideCard({
  title,
  hint,
  children,
}: {
  title: string;
  /** Said once under the heading, where the card has a rule worth stating. */
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="text-sm">{title}</CardTitle>
        {hint ? (
          <p className="text-muted-foreground text-xs font-normal">{hint}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-1 px-4 text-sm">{children}</CardContent>
    </Card>
  );
}

/** What a caller without `customers.view_contact` sees in place of a block. */
function Restricted({ what }: { what: string }) {
  return (
    <p className="text-muted-foreground flex items-start gap-1.5">
      <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span>
        {what} are hidden. Ask an administrator for the “View customer contact
        details” permission.
      </span>
    </p>
  );
}

/**
 * A phone or an email on the Contact card.
 *
 * Marked when it came from the customer's latest order and not from Shopify's
 * customer record (#530): many customers type their phone only into the
 * shipping address. The list shows the same value, so the two agree; the mark
 * is for whoever wonders why the record in Shopify shows none.
 */
function ContactLine({
  value,
  fromOrder,
  className,
}: {
  value: ReactNode;
  fromOrder: boolean | undefined;
  className?: string;
}) {
  return (
    <p className={className}>
      {value}
      {fromOrder ? (
        <span className="text-muted-foreground ml-1.5 text-xs font-normal whitespace-nowrap">
          from latest order
        </span>
      ) : null}
    </p>
  );
}

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-base font-semibold tabular-nums">{children}</p>
    </div>
  );
}

function AddressBlock({ entry }: { entry: CustomerAddress }) {
  return (
    <div className="rounded-md border p-3">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        {entry.roles.map((role) => (
          <Badge key={role} variant="secondary" className="font-normal">
            {role === "shipping" ? "Shipping" : "Billing"}
          </Badge>
        ))}
      </div>
      {addressLines(entry.address).map((line, index) => (
        <p key={`${String(index)}-${line}`} className="break-words">
          {line}
        </p>
      ))}
      {entry.address.phone ? (
        <p>
          <PhoneNumber number={entry.address.phone} name={entry.address.name} />
        </p>
      ) : null}
      <p className="text-muted-foreground mt-1 text-xs">
        Used on {ordersLabel(entry.ordersCount)}, last{" "}
        {dateOnly(entry.lastUsedAt)}
      </p>
    </div>
  );
}

function OrderRow({
  order,
  canOpen,
}: {
  order: CustomerOrder;
  canOpen: boolean;
}) {
  const navigate = useNavigate();
  const cancelled = order.cancelledAt !== null;

  const body = (
    <>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "text-sm font-medium",
              cancelled && "text-muted-foreground line-through",
            )}
          >
            {order.orderNumber}
          </span>
          {cancelled ? (
            <Badge variant="outline" className="font-normal">
              Cancelled
            </Badge>
          ) : null}
        </div>
        <p className="text-muted-foreground text-xs">
          {dateOnly(order.placedAt)} · {order.itemCount} item
          {order.itemCount === 1 ? "" : "s"}
        </p>
        {!cancelled ? (
          <div className="flex flex-wrap gap-1.5">
            {order.financialStatus ? (
              <Badge variant="secondary" className="font-normal">
                {FINANCIAL_LABEL[order.financialStatus] ??
                  order.financialStatus}
              </Badge>
            ) : null}
            <Badge variant="secondary" className="font-normal">
              {order.fulfilmentStatus
                ? (FULFILMENT_LABEL[order.fulfilmentStatus] ??
                  order.fulfilmentStatus)
                : "Unfulfilled"}
            </Badge>
          </div>
        ) : null}
      </div>
      <p
        className={cn(
          "shrink-0 text-sm font-medium tabular-nums",
          cancelled && "text-muted-foreground line-through",
        )}
      >
        {money(order.total)}
      </p>
    </>
  );

  if (!canOpen) {
    return <li className="flex items-start gap-3 py-2">{body}</li>;
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          void navigate({ to: "/orders", search: { record: order.id } });
        }}
        className="hover:bg-muted/50 focus-visible:ring-ring -mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-md px-2 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {body}
      </button>
    </li>
  );
}

function CustomerDetailBody({ customer }: { customer: CustomerWithDetails }) {
  const permissions = usePermissions();
  const canViewContact = can(permissions, "customers.view_contact");
  const canAnnotate = can(permissions, "customers.annotate");
  const canOpenOrders = can(permissions, "orders.list.view");

  return (
    <div className="space-y-4">
      {/*
        The name is the drawer's title (`CustomerList`), not repeated here: the
        bar above this already says it, and a second copy a line below read as
        a layout slip.
      */}
      <header>
        <p className="text-muted-foreground text-sm">
          Customer since {dateOnly(customer.customerSince)}
          {customer.city
            ? ` · ${[customer.city, customer.state].filter(Boolean).join(", ")}`
            : ""}
        </p>
      </header>

      <div className="grid grid-cols-3 gap-2">
        <Figure label="Orders">{customer.ordersCount}</Figure>
        <Figure label="Total spent">{money(customer.totalSpent)}</Figure>
        <Figure label="Last order">{dateOnly(customer.lastOrderAt)}</Figure>
      </div>
      <p className="text-muted-foreground -mt-2 text-xs">
        Cancelled orders are not counted. Customers belong to Shopify and are
        read-only here.
      </p>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4 lg:col-start-1">
          <Card className="gap-3 py-4">
            <CardHeader className="px-4">
              <CardTitle className="text-sm">
                Orders ({customer.ordersTotal})
              </CardTitle>
              {/*
                The server lists at most the latest few hundred. The heading
                is the true count, so when the list is shorter it says so
                rather than letting the heading and the rows disagree quietly.
              */}
              {customer.orders.length < customer.ordersTotal ? (
                <p className="text-muted-foreground text-xs font-normal">
                  Showing the latest {customer.orders.length} of{" "}
                  {customer.ordersTotal} orders.
                </p>
              ) : null}
            </CardHeader>
            <CardContent className="px-4">
              {customer.orders.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No orders on file.
                </p>
              ) : (
                <ul className="divide-y">
                  {customer.orders.map((order) => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      canOpen={canOpenOrders}
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <CommentThread entityType="customer" entityId={customer.id} />
        </div>

        <div className="min-w-0 space-y-4 lg:col-start-2 lg:row-start-1">
          <SideCard title="Contact">
            {canViewContact ? (
              <>
                {customer.email ? (
                  <ContactLine
                    value={customer.email}
                    fromOrder={customer.emailFromOrder}
                    className="break-all"
                  />
                ) : null}
                {customer.phone ? (
                  <ContactLine
                    value={
                      <PhoneNumber
                        number={customer.phone}
                        name={customer.displayName}
                      />
                    }
                    fromOrder={customer.phoneFromOrder}
                  />
                ) : null}
                {!customer.email && !customer.phone ? (
                  <p className="text-muted-foreground">
                    No phone or email on file.
                  </p>
                ) : null}
              </>
            ) : (
              <Restricted what="Phone and email" />
            )}
          </SideCard>

          <SideCard
            title="Addresses"
            hint="From this customer's orders — Shopify keeps no separate address book here."
          >
            {canViewContact ? (
              customer.addresses.length === 0 ? (
                <p className="text-muted-foreground">No addresses on file.</p>
              ) : (
                <div className="space-y-2">
                  {customer.addresses.map((entry, index) => (
                    <AddressBlock
                      key={`${String(index)}-${entry.lastUsedAt}`}
                      entry={entry}
                    />
                  ))}
                </div>
              )
            ) : (
              <Restricted what="Full addresses" />
            )}
          </SideCard>

          <SideCard
            title="Note"
            hint="Only in this ERP. Never sent to Shopify."
          >
            <CustomerNoteEditor
              customerId={customer.id}
              note={customer.note}
              canEdit={canAnnotate}
            />
          </SideCard>

          <SideCard
            title="Tags"
            hint="Only in this ERP. Never sent to Shopify."
          >
            <CustomerTagsEditor
              customerId={customer.id}
              tags={customer.tags}
              canEdit={canAnnotate}
            />
          </SideCard>

          {customer.shopifyTags.length > 0 ? (
            <SideCard title="Shopify tags" hint="Read-only.">
              <div className="flex flex-wrap gap-1.5">
                {customer.shopifyTags.map((tag) => (
                  <Badge key={tag} variant="outline" className="font-normal">
                    {tag}
                  </Badge>
                ))}
              </div>
            </SideCard>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * One customer, opened over the list in a drawer (#447).
 *
 * Fetched by id rather than found in the list's rows, so a link opened cold —
 * from an order, a shared URL, a refresh — lands on a customer the current
 * page does not contain.
 */
export function CustomerDetail({ customerId }: { customerId: string }) {
  const data = useDataSource();

  const query = useQuery({
    queryKey: CUSTOMER_KEYS.detail(customerId),
    queryFn: () => data.customers.get(customerId),
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="bg-muted/30 min-h-0 flex-1 overflow-auto p-3 sm:p-4">
        {query.isPending ? (
          <TableSkeleton rows={4} />
        ) : query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : query.data === null ? (
          <EmptyState
            title="Customer not found"
            description="It may have been removed since this list was loaded."
          />
        ) : (
          <CustomerDetailBody customer={query.data} />
        )}
      </div>
    </div>
  );
}
